import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Trophy, Trash2, Link2Off, ChevronUp, KeyRound,
  Loader2, Search, Zap, Share2, X, Sparkles, History,
} from 'lucide-react';

import Header from '../components/Header';
import Badge from '../components/Badge';
import Tooltip from '../components/Tooltip';
import CookiesInstructionsBox from '../components/CookiesInstructionsBox';
import ReturningLeagueSelector from '../components/ReturningLeagueSelector';
import { safeFetch, fetchWithRetry } from '../utils/api';
import { getCookie } from '../utils/csrf';
import { useAuth } from '../components/AuthContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { setClarityTags, trackClarityEvent } from '../utils/clarity';
import usePageMeta from '../utils/usePageMeta';
import useYahooOAuth from '../utils/useYahooOAuth';
import { useAttribution } from '../components/AttributionContext';

const WELCOME_BANNER_KEY = 'doritostats_welcome_dismissed';
const WELCOME_BANNER_CUTOFF = new Date('2026-10-01T00:00:00');

// Which top-level panel is open: null | 'add' | 'claim'
const PANEL = { ADD: 'add', CLAIM: 'claim' };

const LeagueCardSkeleton = () => (
  <div className="bg-ds-white border border-border-card rounded-card overflow-hidden border-l-[5px] border-l-border animate-pulse">
    <div className="p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="h-2.5 w-12 bg-border rounded" />
        <div className="h-5 w-14 bg-border rounded-full" />
      </div>
      <div className="h-5 w-3/4 bg-border rounded mb-2" />
      <div className="h-3 w-1/2 bg-border/60 rounded mb-4" />
      <div className="flex items-center justify-between">
        <div className="h-4 w-20 bg-border rounded" />
        <div className="flex gap-2">
          <div className="h-4 w-4 bg-border rounded" />
          <div className="h-4 w-4 bg-border rounded" />
          <div className="h-4 w-4 bg-border rounded" />
        </div>
      </div>
    </div>
  </div>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lastYahooErrorRef = useRef('');

  usePageMeta({
    title: 'My Leagues',
    description: 'Manage your ESPN fantasy football leagues and access weekly analytics, luck index, playoff simulations, and more.',
    canonical: '/dashboard',
  });

  // My leagues
  const [leagues, setLeagues] = useState([]);
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  // Inline confirm strip: null | { leagueId, action: 'unlink'|'remove', loading, error }
  const [confirm, setConfirm] = useState(null);

  const closeConfirm = () => setConfirm(null);
  const handleConfirm = async () => {
    setConfirm((c) => ({ ...c, loading: true, error: null }));
    try {
      await confirm.onConfirm();
    } catch (err) {
      setConfirm((c) => c ? { ...c, loading: false, error: err.message } : null);
    }
  };
  if (fetchError) throw fetchError;

  // Which panel is open
  const [openPanel, setOpenPanel] = useState(null);

  // Welcome banner (one-time, dismissible)
  const [showWelcome, setShowWelcome] = useState(
    () => new Date() < WELCOME_BANNER_CUTOFF && !localStorage.getItem(WELCOME_BANNER_KEY)
  );
  const dismissWelcome = () => {
    localStorage.setItem(WELCOME_BANNER_KEY, '1');
    setShowWelcome(false);
  };

  // --- Saved ESPN credentials profile ---
  const [savedCredentials, setSavedCredentials] = useState(null); // null = not yet fetched

  // --- Add League form state ---
  const [formData, setFormData] = useState({
    league_id: '', league_year: 2026, swid: '', espn_s2: '', provider: 'espn',
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  // --- Sleeper username search ---
  const [sleeperUsername, setSleeperUsername] = useState('');
  const [sleeperLeagues, setSleeperLeagues] = useState(null);
  const [sleeperLeaguesLoading, setSleeperLeaguesLoading] = useState(false);
  const [sleeperLeaguesError, setSleeperLeaguesError] = useState('');

  // Report the providers on this dashboard to the global footer so the Yahoo
  // attribution line shows whenever a Yahoo league is listed.
  const { setProviders: setAttributionProviders } = useAttribution();
  useEffect(() => {
    setAttributionProviders(leagues.map((l) => l.provider));
    return () => setAttributionProviders([]);
  }, [leagues, setAttributionProviders]);

  const handleYahooDisconnect = async () => {
    const yahooCount = leagues.filter((l) => l.provider === 'yahoo').length;
    const confirmed = window.confirm(
      yahooCount
        ? `Disconnect Yahoo? This removes your Yahoo tokens and permanently deletes ${yahooCount} Yahoo league${yahooCount === 1 ? '' : 's'} from Doritostats.`
        : 'Disconnect Yahoo? This removes your Yahoo tokens from Doritostats.'
    );
    if (!confirmed) return;
    trackClarityEvent('dashboard_yahoo_disconnected');
    const result = await yahoo.disconnect();
    if (result) {
      toast.success('Yahoo disconnected and Yahoo league data deleted.');
      loadLeagues();
    }
  };

  // --- Yahoo OAuth ---
  const yahoo = useYahooOAuth(formData.league_year, {
    onSuccess: () => {
      trackClarityEvent('dashboard_yahoo_connect_succeeded');
      setOpenPanel(PANEL.ADD);
      setFormData((prev) => ({ ...prev, provider: 'yahoo' }));
      toast.success('Yahoo account connected!');
    },
  });

  // Initialize Yahoo status when the Yahoo provider tab is selected
  useEffect(() => {
    if (formData.provider === 'yahoo') {
      yahoo.initialize(formData.league_year);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.provider]);

  // Re-fetch Yahoo leagues when year changes (if already connected)
  useEffect(() => {
    if (formData.provider === 'yahoo' && yahoo.status === 'connected') {
      yahoo.reloadLeagues(formData.league_year);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.league_year]);

  useEffect(() => {
    setClarityTags({
      feature_surface: 'dashboard',
      has_league_context: 'false',
    });
  }, []);

  useEffect(() => {
    if (openPanel === PANEL.ADD) {
      trackClarityEvent('dashboard_add_panel_opened');
    } else if (openPanel === PANEL.CLAIM) {
      trackClarityEvent('dashboard_claim_panel_opened');
    }
  }, [openPanel]);

  useEffect(() => {
    if (!yahoo.connectError || yahoo.connectError === lastYahooErrorRef.current) return;
    lastYahooErrorRef.current = yahoo.connectError;
    trackClarityEvent('dashboard_yahoo_connect_failed');
  }, [yahoo.connectError]);

  // Handle ?yahoo=connected / ?yahoo=error redirects from OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const yahooParam = params.get('yahoo');
    if (!yahooParam) return;
    window.history.replaceState({}, '', '/dashboard');
    if (window.opener) {
      // We are the popup — notify parent and close
      const type = yahooParam === 'connected' ? 'yahoo_oauth_success' : 'yahoo_oauth_error';
      const reason = params.get('reason') || 'unknown';
      window.opener.postMessage(
        yahooParam === 'connected' ? { type } : { type, reason },
        window.location.origin
      );
      window.close();
    } else if (yahooParam === 'connected') {
      // Direct redirect (not popup) — open add panel with Yahoo pre-selected
      setOpenPanel(PANEL.ADD);
      setFormData((prev) => ({ ...prev, provider: 'yahoo' }));
      toast.success('Yahoo account connected!');
    } else {
      toast.error('Yahoo connection failed. Please try again.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Claim Legacy League state ---
  const [claimableLeagues, setClaimableLeagues] = useState(null); // null = not yet fetched
  const [claimSearch, setClaimSearch] = useState('');
  const [claimingId, setClaimingId] = useState(null); // db pk being claimed
  const [claimedIds, setClaimedIds] = useState(new Set()); // db pks already claimed this session
  const [autoClaimLoading, setAutoClaimLoading] = useState(false);

  // --- Import All Seasons state ---
  // Set of ESPN league_ids currently being imported (prevents double-submission)
  const [importingLeagueIds, setImportingLeagueIds] = useState(new Set());

  const loadLeagues = () => {
    safeFetch('/api/leagues/', {}, false, 2)
      .then((data) => {
        const all = [
          ...(data.leagues_current_year || []),
          ...(data.leagues_previous_year || []),
        ];
        setLeagues(all);
        setLeaguesLoading(false);
      })
      .catch((err) => { console.error('Error fetching leagues:', err); setLeaguesLoading(false); setFetchError(err); });
  };

  useEffect(() => {
    fetch('/api/get-csrf-token/', { credentials: 'include' }).catch(() => {});
    loadLeagues();
    // Fetch saved ESPN credentials for the Add League form pre-fill
    safeFetch('/api/auth/profile/', {}, false, 1)
      .then((data) => {
        setSavedCredentials(data);
        if (data.swid || data.espn_s2) {
          setFormData((prev) => ({
            ...prev,
            swid: data.swid || prev.swid,
            espn_s2: data.espn_s2 || prev.espn_s2,
          }));
        }
        if (data.sleeper_username) {
          setSleeperUsername(data.sleeper_username);
        }
      })
      .catch(() => setSavedCredentials({}));
  }, []);

  // Fetch claimable leagues the first time the claim panel is opened
  useEffect(() => {
    if (openPanel === PANEL.CLAIM && claimableLeagues === null) {
      safeFetch('/api/leagues/claimable/', {}, false, 2)
        .then((data) => setClaimableLeagues(Array.isArray(data) ? data : []))
        .catch(() => setClaimableLeagues([]));
    }
  }, [openPanel, claimableLeagues]);

  const togglePanel = (panel) => {
    setOpenPanel((prev) => (prev === panel ? null : panel));
    // Reset form states when switching
    setFormError('');
    setClaimSearch('');
  };

  // --- Handlers: Remove league (hard delete) ---
  const handleRemove = (leagueDbId) => {
    setConfirm({
      leagueId: leagueDbId,
      action: 'remove',
      loading: false,
      error: null,
      onConfirm: async () => {
        const csrftoken = getCookie('csrftoken');
        const response = await fetchWithRetry(
          `/api/league/${leagueDbId}/delete/`,
          { method: 'POST', headers: { 'X-CSRFToken': csrftoken }, credentials: 'include' },
          1
        );
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || `Server error (${response.status})`);
        }
        setLeagues((prev) => prev.filter((l) => l.id !== leagueDbId));
        closeConfirm();
        toast.success('League deleted');
      },
    });
  };

  // --- Handlers: Unlink league (releases back to unclaimed pool) ---
  const handleUnlink = (leagueDbId) => {
    setConfirm({
      leagueId: leagueDbId,
      action: 'unlink',
      loading: false,
      error: null,
      onConfirm: async () => {
        const csrftoken = getCookie('csrftoken');
        const response = await fetchWithRetry(
          `/api/league/${leagueDbId}/unlink/`,
          { method: 'POST', headers: { 'X-CSRFToken': csrftoken }, credentials: 'include' },
          1
        );
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || `Server error (${response.status})`);
        }
        const data = await response.json();
        setLeagues((prev) => prev.filter((l) => l.id !== leagueDbId));
        // Add the released league to the claimable list if it has already been loaded
        if (data.league) {
          setClaimableLeagues((prev) =>
            prev
              ? [...prev, data.league].sort((a, b) =>
                  a.league_name.localeCompare(b.league_name) || b.league_year - a.league_year
                )
              : prev
          );
        }
        closeConfirm();
        toast.success('League released');
      },
    });
  };

  // --- Handlers: Share league (generates token, copies URL to clipboard) ---
  const handleShare = async (leagueDbId) => {
    try {
      const response = await fetchWithRetry(
        `/api/league/${leagueDbId}/share/`,
        { method: 'POST' },
        1
      );
      const data = await response.json();
      if (response.ok) {
        trackClarityEvent('dashboard_share_link_generated');
        // The API may return a path-only URL (empty FRONTEND_URL); resolve it
        // against the current site so the copied link is shareable.
        const shareUrl = new URL(data.url, window.location.origin).href;
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Share link copied to clipboard!');
        } catch {
          window.prompt('Copy share link:', shareUrl);
          toast.success('Share link ready to copy.');
        }
      } else {
        toast.error(data.error || 'Failed to generate share link.');
      }
    } catch {
      toast.error('An unexpected error occurred.');
    }
  };

  // --- Handlers: Unsave league (for saved-but-not-owned leagues) ---
  const handleUnsave = (leagueDbId) => {
    setConfirm({
      leagueId: leagueDbId,
      action: 'unsave',
      loading: false,
      error: null,
      onConfirm: async () => {
        const csrftoken = getCookie('csrftoken');
        const response = await fetchWithRetry(
          `/api/league/${leagueDbId}/unsave/`,
          { method: 'POST', headers: { 'X-CSRFToken': csrftoken }, credentials: 'include' },
          1
        );
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || `Server error (${response.status})`);
        }
        setLeagues((prev) => prev.filter((l) => l.id !== leagueDbId));
        closeConfirm();
        toast.success('League removed from dashboard');
      },
    });
  };

  // --- Handlers: Import All Seasons ---
  const handleImportAllSeasons = async (espnLeagueId) => {
    trackClarityEvent('dashboard_import_all_seasons_started');
    setImportingLeagueIds((prev) => new Set([...prev, espnLeagueId]));
    try {
      const csrftoken = getCookie('csrftoken');
      const response = await fetchWithRetry(
        `/api/league/${espnLeagueId}/import-all-seasons/`,
        { method: 'POST', headers: { 'X-CSRFToken': csrftoken }, credentials: 'include' },
        1
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        const parts = [];
        if (data.imported?.length)       parts.push(`${data.imported.length} season${data.imported.length !== 1 ? 's' : ''} imported`);
        if (data.already_existed?.length) parts.push(`${data.already_existed.length} already existed`);
        if (data.skipped?.length)         parts.push(`${data.skipped.length} not available`);
        toast.success(parts.length ? parts.join(' · ') : 'Nothing new to import');
        loadLeagues();
      } else {
        toast.error(data.error || 'Import failed. Please try again.');
      }
    } catch {
      toast.error('Import failed. Please try again.');
    } finally {
      setImportingLeagueIds((prev) => {
        const next = new Set(prev);
        next.delete(espnLeagueId);
        return next;
      });
    }
  };

  // --- Handlers: Add League ---
  const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // Reset Sleeper username search when switching away from Sleeper provider or changing year
  // Restore the saved username when switching back to the Sleeper tab.
  useEffect(() => {
    setSleeperUsername(savedCredentials?.sleeper_username || '');
    setSleeperLeagues(null);
    setSleeperLeaguesError('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.provider, formData.league_year]);

  const handleSleeperUsernameSearch = async () => {
    if (!sleeperUsername.trim()) return;
    setSleeperLeaguesLoading(true);
    setSleeperLeaguesError('');
    setSleeperLeagues(null);
    try {
      const data = await safeFetch(`/api/auth/sleeper/leagues/?username=${encodeURIComponent(sleeperUsername.trim())}&year=${formData.league_year}`);
      if (data.error === 'sleeper_user_not_found') {
        setSleeperLeaguesError(`No Sleeper user found with username "${sleeperUsername.trim()}".`);
      } else {
        setSleeperLeagues(data.leagues ?? []);
        if ((data.leagues ?? []).length === 0) {
          setSleeperLeaguesError(`No Sleeper leagues found for "${sleeperUsername.trim()}" in ${formData.league_year}.`);
        } else {
          // Save the username to the user's profile for future auto-fill.
          const trimmed = sleeperUsername.trim();
          if (trimmed !== savedCredentials?.sleeper_username) {
            getCookie('csrftoken') && fetchWithRetry('/api/auth/profile/', {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCookie('csrftoken') },
              credentials: 'include',
              body: JSON.stringify({ sleeper_username: trimmed }),
            }, 1).then(() => setSavedCredentials((prev) => ({ ...prev, sleeper_username: trimmed }))).catch(() => {});
          }
        }
      }
    } catch {
      setSleeperLeaguesError('Failed to fetch Sleeper leagues. Please check the username and try again.');
    } finally {
      setSleeperLeaguesLoading(false);
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    trackClarityEvent('dashboard_add_league_submitted');
    try {
      const csrftoken = getCookie('csrftoken');
      // Build a provider-scoped payload so ESPN credentials are never sent
      // for non-ESPN leagues (the form pre-fills swid/espn_s2 for all providers).
      const payload = {
        provider:     formData.provider,
        league_id:    formData.league_id,
        league_year:  formData.league_year,
        ...(formData.provider === 'espn' && { swid: formData.swid, espn_s2: formData.espn_s2 }),
      };
      const response = await fetchWithRetry(
        '/api/league-input/',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrftoken },
          credentials: 'include',
          body: JSON.stringify(payload),
        },
        2
      );
      const data = await response.json();
      if (response.status === 409 && data.code === 'too_soon_league') {
        navigate(`/fantasy_stats/uh-oh-too-early/league-homepage/${formData.provider}/${formData.league_year}/${formData.league_id}`);
      } else if (response.ok && data.redirect_url) {
        navigate(data.redirect_url);
      } else {
        setFormError(data.error || 'Failed to add league.');
      }
    } catch {
      setFormError('An unexpected error occurred.');
    } finally {
      setFormLoading(false);
    }
  };

  // --- Handlers: Claim Legacy League ---
  const handleClaim = async (league) => {
    trackClarityEvent('dashboard_claim_legacy_submitted');
    setClaimingId(league.id);
    try {
      const csrftoken = getCookie('csrftoken');
      const response = await fetchWithRetry(
        '/api/league/claim/',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrftoken },
          credentials: 'include',
          body: JSON.stringify({ league_id: league.league_id, league_year: league.league_year }),
        },
        1
      );
      const data = await response.json();
      if (response.ok && data.success) {
        setClaimedIds((prev) => new Set([...prev, league.id]));
        loadLeagues();
        toast.success(`"${league.league_name}" claimed`);
      } else {
        // Surface error inline — use a per-item error state
        setClaimableLeagues((prev) =>
          prev.map((l) => l.id === league.id ? { ...l, error: data.error || 'Failed to claim.' } : l)
        );
      }
    } catch {
      setClaimableLeagues((prev) =>
        prev.map((l) => l.id === league.id ? { ...l, error: 'An unexpected error occurred.' } : l)
      );
    } finally {
      setClaimingId(null);
    }
  };

  // --- Handlers: Auto-claim by saved SWID ---
  const handleAutoClaim = async () => {
    trackClarityEvent('dashboard_auto_claim_started');
    setAutoClaimLoading(true);
    try {
      const csrftoken = getCookie('csrftoken');
      const response = await fetchWithRetry(
        '/api/league/auto-claim/',
        { method: 'POST', headers: { 'X-CSRFToken': csrftoken }, credentials: 'include' },
        1
      );
      const data = await response.json();
      if (response.ok) {
        if (data.claimed > 0) {
          // One toast per claimed league so the user knows exactly what was found
          data.leagues.forEach((l) => {
            toast.success(`"${l.league_name}" (${l.league_year}) auto-claimed`);
          });
          // Reload the My Leagues section
          loadLeagues();
          // Remove claimed leagues from the claimable list directly — no re-fetch needed
          const autoClaimedIds = new Set(data.leagues.map((l) => l.id));
          setClaimableLeagues((prev) =>
            prev ? prev.filter((l) => !autoClaimedIds.has(l.id)) : prev
          );
        } else {
          toast.success('No new leagues found matching your SWID');
        }
      } else {
        toast.error(data.message || 'Auto-claim failed');
      }
    } catch {
      toast.error('Auto-claim failed');
    } finally {
      setAutoClaimLoading(false);
    }
  };

  // Filtered claimable leagues (exclude already-claimed this session)
  const filteredClaimable = useMemo(() => {
    if (!claimableLeagues) return [];
    const q = claimSearch.trim().toLowerCase();
    return claimableLeagues
      .filter((l) => !claimedIds.has(l.id))
      .filter((l) =>
        !q ||
        l.league_name.toLowerCase().includes(q) ||
        String(l.league_id).includes(q)
      );
  }, [claimableLeagues, claimSearch, claimedIds]);

  // Grouping / display helpers
  const byYear = leagues.reduce((acc, l) => {
    const y = l.league_year;
    if (!acc[y]) acc[y] = [];
    acc[y].push(l);
    return acc;
  }, {});
  const sortedYears = Object.keys(byYear).sort((a, b) => b - a);
  const currentYear = new Date().getFullYear();
  const activeCount = leagues.filter((l) => l.league_year >= currentYear).length;

  const firstName = user?.email ? user.email.split('@')[0].split('.')[0] : null;
  const displayName = firstName
    ? firstName.charAt(0).toUpperCase() + firstName.slice(1)
    : 'there';

  return (
    <div className="min-h-screen flex flex-col bg-bg">

      <Header />

      {/* Hero band */}
      <div className="bg-green px-6 py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="font-display text-2xl font-black text-white leading-none">
            Welcome back, <span className="text-mustard italic">{displayName}</span> 🏈
          </div>
          <div className="flex gap-10">
            <div className="text-center">
              <div className="font-display text-[1.75rem] font-black text-white leading-none">
              {leaguesLoading ? <span className="inline-block w-6 h-7 bg-white/20 rounded animate-pulse" /> : leagues.length}
            </div>
              <div className="text-[0.68rem] font-bold tracking-[0.1em] uppercase text-white/60 mt-0.5">Leagues</div>
            </div>
            <div className="text-center">
              <div className="font-display text-[1.75rem] font-black text-mustard leading-none">
              {leaguesLoading ? <span className="inline-block w-6 h-7 bg-mustard/30 rounded animate-pulse" /> : activeCount}
            </div>
              <div className="text-[0.68rem] font-bold tracking-[0.1em] uppercase text-white/60 mt-0.5">Active</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-10">

        {/* Welcome banner — shown once to returning users after the 2026 redesign */}
        {showWelcome && (
          <div className="mb-8 bg-mustard-light border border-mustard/40 border-l-[5px] border-l-mustard rounded-card p-5 shadow-card flex items-start gap-4">
            <Sparkles className="w-6 h-6 text-mustard shrink-0 mt-0.5 animate-twinkle" />
            <div className="flex-1 min-w-0">
              <p className="font-display text-base font-bold text-ds-text mb-1">
                Welcome to the new Doritostats!
              </p>
              <p className="text-sm text-text-mid leading-relaxed">
                The site got a fresh redesign for 2026 — same stats and leagues you know, just a new look.
                If you had leagues on the old site, you can reclaim them in seconds.
              </p>
              <button
                onClick={() => { togglePanel(PANEL.CLAIM); dismissWelcome(); }}
                className="mt-3 inline-flex items-center gap-1.5 bg-forest text-mustard font-extrabold text-xs px-4 py-1.5 rounded-lg hover:bg-forest/90 transition-colors"
              >
                <KeyRound className="w-3.5 h-3.5" />
                Claim your old leagues
              </button>
            </div>
            <button
              onClick={dismissWelcome}
              aria-label="Dismiss"
              className="shrink-0 text-text-muted hover:text-ds-text transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Page header */}
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-5xl font-black text-ds-text leading-none">
              My <span className="text-green italic">Leagues</span>
            </h1>
            <p className="text-text-muted text-sm font-semibold mt-1">
              {leagues.length} league{leagues.length !== 1 ? 's' : ''} across{' '}
              {sortedYears.length} season{sortedYears.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => togglePanel(PANEL.ADD)}
              className={cn(
                'flex items-center gap-2 font-extrabold text-sm px-5 py-2.5 rounded-lg transition-colors',
                openPanel === PANEL.ADD
                  ? 'bg-forest text-mustard hover:bg-forest/90'
                  : 'bg-mustard text-forest shadow-mustard hover:bg-yellow-500'
              )}
            >
              {openPanel === PANEL.ADD
                ? <><ChevronUp className="w-4 h-4" /> Cancel</>
                : <><Plus className="w-4 h-4" /> Add League</>
              }
            </button>

            <button
              onClick={() => togglePanel(PANEL.CLAIM)}
              className={cn(
                'flex items-center gap-2 font-extrabold text-sm px-5 py-2.5 rounded-lg border transition-colors',
                openPanel === PANEL.CLAIM
                  ? 'bg-forest text-mustard border-forest hover:bg-forest/90'
                  : 'bg-ds-white text-forest border-border-card hover:border-mustard hover:text-mustard'
              )}
            >
              <KeyRound className="w-4 h-4" />
              Claim Legacy League
            </button>
          </div>
        </div>

        {/* Add League panel */}
        {openPanel === PANEL.ADD && (
          <div className="mb-8 bg-ds-white border border-border-card rounded-card p-6 shadow-card">
            <h2 className="font-display text-xl font-bold text-ds-text mb-4">Add a League</h2>
            <form onSubmit={handleAddSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Provider selector */}
              <div className="sm:col-span-2 mb-4">
                <label className="block text-xs font-bold text-text-muted uppercase tracking-widest mb-2">Provider</label>
                <div className="flex gap-3 flex-wrap">
                  {[
                    { value: 'espn',    label: 'ESPN',    active: true },
                    { value: 'sleeper', label: 'Sleeper', active: true },
                    { value: 'yahoo',   label: 'Yahoo',   active: true },
                  ].map(({ value, label, active }) => (
                    <button
                      key={value}
                      type="button"
                      disabled={!active}
                      onClick={() => active && handleFormChange({ target: { name: 'provider', value } })}
                      className={cn(
                        'px-4 py-1.5 rounded-full text-sm font-bold border transition-colors',
                        formData.provider === value
                          ? 'bg-green text-white border-green'
                          : active
                            ? 'bg-ds-white text-ds-text border-border hover:border-green'
                            : 'bg-ds-bg text-text-muted border-border opacity-50 cursor-not-allowed'
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* League ID — shown for ESPN only; Sleeper gets its own block; Yahoo has a picker */}
              {formData.provider !== 'yahoo' && formData.provider !== 'sleeper' && (
                <div>
                  <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1">League ID</label>
                  <input type="text" name="league_id" value={formData.league_id} onChange={handleFormChange}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green" />
                </div>
              )}

              {/* Sleeper: username search + league picker + League ID */}
              {formData.provider === 'sleeper' && (
                <div className="sm:col-span-2 space-y-3">
                  {/* Username search */}
                  <div>
                    <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1">
                      Sleeper Username
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={sleeperUsername}
                        onChange={(e) => setSleeperUsername(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleSleeperUsernameSearch())}
                        placeholder="Your Sleeper username"
                        className="flex-1 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green"
                      />
                      <button
                        type="button"
                        onClick={handleSleeperUsernameSearch}
                        disabled={sleeperLeaguesLoading || !sleeperUsername.trim()}
                        className="px-4 py-2 bg-green text-white text-sm font-bold rounded-lg disabled:opacity-50 transition-colors"
                      >
                        {sleeperLeaguesLoading ? <Loader2 size={15} className="animate-spin" /> : 'Find'}
                      </button>
                    </div>
                    {sleeperLeaguesError && <p className="text-coral text-xs mt-1">{sleeperLeaguesError}</p>}
                  </div>

                  {/* League picker from search results */}
                  {sleeperLeagues && sleeperLeagues.length > 0 && (
                    <div>
                      <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1">Select League</label>
                      <select
                        value={formData.league_id}
                        onChange={(e) => setFormData((prev) => ({ ...prev, league_id: e.target.value }))}
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green bg-ds-white"
                      >
                        <option value="">Choose a league…</option>
                        {sleeperLeagues.map((l) => (
                          <option key={l.league_id} value={l.league_id}>
                            {l.name} ({l.num_teams} teams)
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* League ID (manual / pre-filled) */}
                  <div>
                    <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1">League ID</label>
                    <input
                      type="text"
                      name="league_id"
                      value={formData.league_id}
                      onChange={handleFormChange}
                      placeholder="18-digit Sleeper league ID"
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1">Season Year</label>
                <input type="number" min="2017" name="league_year" value={formData.league_year} onChange={handleFormChange}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green" />
              </div>

              {/* ESPN-only credentials */}
              {formData.provider === 'espn' && (
                <div className="contents" data-clarity-mask="true">
                  <div>
                    <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1">
                      SWID
                      {savedCredentials?.swid && (
                        <span className="ml-2 text-[0.65rem] font-bold text-green normal-case tracking-normal">
                          ✓ saved
                        </span>
                      )}
                    </label>
                    <input type="text" name="swid" value={formData.swid} onChange={handleFormChange}
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1">
                      ESPN s2
                      {savedCredentials?.espn_s2 && (
                        <span className="ml-2 text-[0.65rem] font-bold text-green normal-case tracking-normal">
                          ✓ saved
                        </span>
                      )}
                    </label>
                    <input type="text" name="espn_s2" value={formData.espn_s2} onChange={handleFormChange}
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green" />
                  </div>
                </div>
              )}

              {/* Yahoo OAuth connect + league picker */}
              {formData.provider === 'yahoo' && (
                <div className="sm:col-span-2">
                  {/* Checking status */}
                  {yahoo.status === 'checking' && (
                    <div className="flex items-center gap-2 text-sm text-text-muted py-2">
                      <Loader2 size={15} className="animate-spin" />
                      Checking Yahoo connection…
                    </div>
                  )}

                  {/* Not connected — show connect button */}
                  {(yahoo.status === 'disconnected' || yahoo.status === 'idle') && !yahoo.connecting && (
                    <div className="space-y-3">
                      <p className="text-sm text-text-muted">
                        Connect your Yahoo account to browse your leagues.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          trackClarityEvent('dashboard_yahoo_connect_started');
                          yahoo.startOAuth(formData.league_year);
                        }}
                        className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm px-5 py-2.5 rounded-lg transition-colors"
                      >
                        Connect Yahoo Account
                      </button>
                      {yahoo.connectError && (
                        <p className="text-coral text-sm font-semibold">{yahoo.connectError}</p>
                      )}
                    </div>
                  )}

                  {/* Waiting for popup */}
                  {yahoo.connecting && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-text-muted">
                        <Loader2 size={15} className="animate-spin" />
                        Waiting for Yahoo authorization in the other window…
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          trackClarityEvent('dashboard_yahoo_connect_started');
                          yahoo.startOAuth(formData.league_year);
                        }}
                        className="text-xs text-text-muted underline"
                      >
                        Didn't see a window? Try again
                      </button>
                    </div>
                  )}

                  {/* Connected — show league picker */}
                  {yahoo.status === 'connected' && !yahoo.connecting && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-green font-semibold">
                        ✓ Yahoo Connected
                        <button
                          type="button"
                          onClick={() => {
                            trackClarityEvent('dashboard_yahoo_connect_started');
                            yahoo.startOAuth(formData.league_year);
                          }}
                          className="text-xs text-text-muted font-normal underline ml-2"
                        >
                          Reconnect
                        </button>
                        <button
                          type="button"
                          onClick={handleYahooDisconnect}
                          disabled={yahoo.disconnecting}
                          className="text-xs text-coral font-normal underline ml-1 disabled:opacity-60"
                          data-testid="yahoo-disconnect"
                        >
                          {yahoo.disconnecting ? 'Disconnecting…' : 'Disconnect Yahoo'}
                        </button>
                      </div>

                      {yahoo.leaguesLoading && (
                        <div className="flex items-center gap-2 text-sm text-text-muted">
                          <Loader2 size={14} className="animate-spin" /> Loading your leagues…
                        </div>
                      )}

                      {yahoo.leaguesError && (
                        <p className="text-coral text-sm font-semibold">{yahoo.leaguesError}</p>
                      )}

                      {yahoo.leagues && !yahoo.leaguesLoading && (
                        yahoo.leagues.length === 0 ? (
                          <p className="text-sm text-text-muted">
                            No Yahoo leagues found for {formData.league_year}. Try a different year.
                          </p>
                        ) : (
                          <div>
                            <label className="block text-xs font-bold text-text-muted uppercase tracking-wider mb-1">
                              Select League
                            </label>
                            <select
                              value={formData.league_id}
                              onChange={(e) => setFormData((prev) => ({ ...prev, league_id: e.target.value }))}
                              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green bg-ds-white"
                            >
                              <option value="">Choose a league…</option>
                              {yahoo.leagues.map((l) => (
                                <option key={l.league_id} value={l.league_id}>
                                  {l.name} ({l.num_teams} teams)
                                </option>
                              ))}
                            </select>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              )}

              {formError && <p className="sm:col-span-2 text-coral text-sm font-semibold">{formError}</p>}
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={
                    formLoading ||
                    (formData.provider === 'yahoo' && !formData.league_id)
                  }
                  className="bg-mustard text-forest font-extrabold text-sm px-6 py-2.5 rounded-lg shadow-mustard hover:bg-yellow-500 transition-colors disabled:opacity-60"
                >
                  {formLoading
                    ? <><Loader2 size={15} className="animate-spin inline-block mr-1.5" />Adding…</>
                    : 'Add League'}
                </button>
              </div>
            </form>
            {formData.provider === 'espn' && <div className="mt-4"><CookiesInstructionsBox provider={formData.provider} /></div>}
            {formData.provider === 'sleeper' && <div className="mt-4"><CookiesInstructionsBox provider={formData.provider} /></div>}
          </div>
        )}

        {/* Claim Legacy League panel */}
        {openPanel === PANEL.CLAIM && (
          <div className="mb-8 bg-ds-white border border-border-card rounded-card shadow-card overflow-hidden">
            <div className="px-6 py-5 border-b border-border flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-xl font-bold text-ds-text mb-1">Claim a Legacy League</h2>
                <p className="text-text-muted text-xs font-semibold">
                  Search by league name or ID. These are leagues that existed before accounts were added — no credentials needed.
                </p>
              </div>
              {savedCredentials?.swid && (
                <button
                  onClick={handleAutoClaim}
                  disabled={autoClaimLoading}
                  className="shrink-0 flex items-center gap-2 bg-forest text-mustard font-extrabold text-xs px-4 py-2 rounded-lg hover:bg-forest/90 transition-colors disabled:opacity-60"
                  title="Find all unclaimed leagues that match your saved SWID"
                >
                  {autoClaimLoading
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…</>
                    : <><Zap className="w-3.5 h-3.5" /> Find My Leagues</>
                  }
                </button>
              )}
            </div>

            {/* Search */}
            <div className="px-6 py-4 border-b border-border">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
                <input
                  type="text"
                  value={claimSearch}
                  onChange={(e) => setClaimSearch(e.target.value)}
                  placeholder="Search by name or league ID…"
                  autoFocus
                  className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:border-green"
                />
              </div>
            </div>

            {/* Results */}
            <div className="divide-y divide-border max-h-80 overflow-y-auto">
              {claimableLeagues === null ? (
                <div className="flex items-center justify-center gap-2 py-10 text-text-muted text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : filteredClaimable.length === 0 ? (
                <div className="py-10 text-center text-text-muted text-sm font-semibold">
                  {claimSearch
                    ? `No unclaimed leagues match "${claimSearch}".`
                    : 'No unclaimed leagues found.'}
                </div>
              ) : (
                filteredClaimable.map((league) => (
                  <div key={league.id} className="flex items-center justify-between px-6 py-3 hover:bg-surface gap-4">
                    <div className="min-w-0">
                      <div className="font-display text-sm font-bold text-ds-text truncate">{league.league_name}</div>
                      <div className="text-xs text-text-muted font-semibold">
                        {league.league_year} · ID {league.league_id}
                      </div>
                      {league.error && (
                        <div className="text-xs text-coral font-semibold mt-0.5">{league.error}</div>
                      )}
                    </div>
                    <button
                      onClick={() => handleClaim(league)}
                      disabled={claimingId === league.id}
                      className="shrink-0 flex items-center gap-1.5 bg-mustard text-forest font-extrabold text-xs px-4 py-1.5 rounded-lg shadow-mustard hover:bg-yellow-500 transition-colors disabled:opacity-60"
                    >
                      {claimingId === league.id
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Claiming…</>
                        : 'Claim'
                      }
                    </button>
                  </div>
                ))
              )}
            </div>

          </div>
        )}

        {/* Returning season copy-over — only when user has leagues */}
        {leagues.length > 0 && <ReturningLeagueSelector />}

        {/* League list */}
        {leaguesLoading ? (
          <div>
            {/* Current year skeleton */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-7 w-14 bg-border rounded-full animate-pulse" />
                <div className="flex-1 h-[1.5px] bg-border" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <LeagueCardSkeleton />
                <LeagueCardSkeleton />
                <LeagueCardSkeleton />
              </div>
            </div>
            {/* Previous year skeleton */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-7 w-14 bg-border rounded-full animate-pulse" />
                <div className="flex-1 h-[1.5px] bg-border" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <LeagueCardSkeleton />
                <LeagueCardSkeleton />
              </div>
            </div>
          </div>
        ) : leagues.length === 0 ? (
          <div className="text-center py-20">
            <Trophy className="w-12 h-12 text-border mx-auto mb-4" />
            <p className="font-display text-xl font-bold text-text-muted">No leagues yet</p>
            <p className="text-text-muted text-sm mt-1">Click "Add League" to get started.</p>
          </div>
        ) : (
          sortedYears.map((year) => {
            const isCurrentYear = parseInt(year) >= currentYear;
            return (
              <div key={year} className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Badge variant="accent" className="text-sm font-bold px-3 py-1 font-display whitespace-nowrap">
                    {year}
                  </Badge>
                  <div className="flex-1 h-[1.5px] bg-border" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {byYear[year].map((league) => (
                    <div
                      key={league.id}
                      className={cn(
                        'bg-ds-white border border-border-card rounded-card overflow-hidden transition-all duration-150',
                        isCurrentYear
                          ? 'border-l-[5px] border-l-green hover:shadow-card hover:border-l-mustard'
                          : 'border-l-[5px] border-l-border hover:shadow-card'
                      )}
                    >
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <span className={cn(
                            'text-[0.7rem] font-extrabold tracking-widest uppercase',
                            league.provider === 'espn'    ? 'text-provider-espn' :
                            league.provider === 'sleeper' ? 'text-provider-sleeper' :
                            league.provider === 'yahoo'   ? 'text-provider-yahoo' :
                            'text-text-muted'
                          )}>{league.provider?.toUpperCase() || 'ESPN'}</span>
                          <Badge variant={isCurrentYear ? 'success' : 'muted'}>
                            {isCurrentYear ? 'Active' : 'Ended'}
                          </Badge>
                        </div>
                        <div className="font-display text-lg font-bold text-ds-text mb-1">{league.league_name}</div>
                        <div className="text-xs text-text-muted font-semibold mb-3">League ID: {league.league_id}</div>
                        <div className="flex items-center justify-between">
                          <Link
                            to={`/fantasy_stats/league/${league.provider || 'espn'}/${league.league_year}/${league.league_id}/`}
                            className="text-green text-sm font-bold hover:text-mustard transition-colors"
                          >
                            View Stats →
                          </Link>
                          <div className="flex items-center gap-2">
                            {league.is_owner !== false && (
                              <Tooltip label="Copy share link">
                                <button
                                  onClick={() => handleShare(league.id)}
                                  className="text-text-muted hover:text-green transition-colors"
                                >
                                  <Share2 className="w-4 h-4" />
                                </button>
                              </Tooltip>
                            )}
                            {league.is_owner !== false && (
                              <Tooltip label="Import all seasons">
                                <button
                                  onClick={() => handleImportAllSeasons(league.league_id)}
                                  disabled={importingLeagueIds.has(league.league_id)}
                                  className="text-text-muted hover:text-mustard transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {importingLeagueIds.has(league.league_id)
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : <History className="w-4 h-4" />}
                                </button>
                              </Tooltip>
                            )}
                            {league.is_owner !== false && (
                              <Tooltip label="Release to unclaimed pool">
                                <button
                                  onClick={() => confirm?.leagueId === league.id && confirm.action === 'unlink' ? closeConfirm() : handleUnlink(league.id)}
                                  className={cn(
                                    'transition-colors',
                                    confirm?.leagueId === league.id && confirm.action === 'unlink'
                                      ? 'text-text-mid'
                                      : 'text-text-muted hover:text-text-mid'
                                  )}
                                >
                                  <Link2Off className="w-4 h-4" />
                                </button>
                              </Tooltip>
                            )}
                            <Tooltip label={league.is_owner === false ? 'Remove from dashboard' : 'Permanently delete league'}>
                              <button
                                onClick={() => {
                                  if (league.is_owner === false) {
                                    confirm?.leagueId === league.id && confirm.action === 'unsave' ? closeConfirm() : handleUnsave(league.id);
                                  } else {
                                    confirm?.leagueId === league.id && confirm.action === 'remove' ? closeConfirm() : handleRemove(league.id);
                                  }
                                }}
                                className={cn(
                                  'transition-colors',
                                  confirm?.leagueId === league.id && confirm.action === 'remove'
                                    ? 'text-coral'
                                    : 'text-text-muted hover:text-coral'
                                )}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      </div>

                      {/* Inline confirm strip */}
                      {confirm?.leagueId === league.id && (
                        <div className={cn(
                          'border-t px-4 py-3 flex items-center justify-between gap-3',
                          confirm.action === 'remove' || confirm.action === 'unsave'
                            ? 'bg-red-50 border-red-100'
                            : 'bg-surface border-border'
                        )}>
                          <span className="text-xs font-semibold text-text-muted leading-snug">
                            {confirm.action === 'remove'
                              ? 'Permanently delete?'
                              : confirm.action === 'unsave'
                              ? 'Remove from dashboard?'
                              : 'Release to unclaimed pool?'}
                          </span>
                          {confirm.error && (
                            <span className="text-xs text-coral font-semibold truncate">{confirm.error}</span>
                          )}
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={closeConfirm}
                              disabled={confirm.loading}
                              className="text-xs font-bold text-text-muted hover:text-ds-text transition-colors disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleConfirm}
                              disabled={confirm.loading}
                              className={cn(
                                'text-xs font-bold px-3 py-1 rounded transition-colors disabled:opacity-50',
                                confirm.action === 'remove' || confirm.action === 'unsave'
                                  ? 'bg-coral text-white hover:bg-red-700'
                                  : 'bg-forest text-white hover:bg-forest/80'
                              )}
                            >
                              {confirm.loading
                                ? 'Working…'
                                : confirm.action === 'remove'
                                ? 'Delete'
                                : confirm.action === 'unsave'
                                ? 'Remove'
                                : 'Release'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}

        {/* Playoff Pool link — coming soon */}
        <div className="mt-10 bg-ds-white border border-border-card rounded-card p-6 border-l-[5px] border-l-border opacity-60">
          <div className="font-display text-xl font-bold text-ds-text mb-1">NFL Playoff Pool</div>
          <p className="text-text-muted text-sm font-semibold mb-4">
            Draft players from playoff teams and compete with your friends!
          </p>
          <button
            disabled
            className="inline-block bg-border text-text-muted font-extrabold text-sm px-5 py-2.5 rounded-lg cursor-not-allowed"
          >
            Coming Soon
          </button>
        </div>

      </main>
    </div>
  );
};

export default DashboardPage;
